import React, { useState, useRef, useEffect } from "react";
import avatar from "../../../assets/3d_avatar_26.png";
import NewSiteForm from "../NewSiteForm";
import sortIcon from "../../../assets/sort-desc.svg";
import styles from "./Projects.module.css";
import { FiArchive } from "react-icons/fi";

import { BASE_URL } from "../../../utils/constant";
import { csrftoken } from "../../../utils/DjangoCSRFToken";
import { MdOutlineArchive, MdOutlineUnarchive } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import OrganizationEmptyState from "components/UI_components/OrganizationEmptyState";
import { OrgAPI } from "api/dashboard/service";
import { ProjectApi } from "api/projects/service";
import ProjectEmptyIcon from "assets/empty state/NoProject.svg";
import Loader from "components/UI_components/Loader";
import LoadingScreen from "components/UI_components/LoadingScreen";
import { recordProjectAccess, getRecentProjectIds, formatCurrency } from "../../../utils/utilityFunctions";
const statusColors = {
  Ongoing: "#F9B233",
  Completed: "#3FC06D",
  "Not Started": "#F55353",
};

// --- Dynamic Projects Fetching ---

const getOrganizationId = () => {
  try {
    return JSON.parse(localStorage.getItem("organization"))?.id;
  } catch {
    return null;
  }
};

const getUserData = () => {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch {
    return null;
  }
};

export default function Projects({
  organizationId: organizationIdProp,
  currentMember,
}) {
  const navigate = useNavigate();
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [projects, setProjects] = useState(
    JSON.parse(localStorage.getItem("projects")) || [],
  );
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("Recent");
  const [sortDropdown, setSortDropdown] = useState(false);
  const sortRef = useRef();
  const [activeTab, setActiveTab] = useState("active");
  const [archiveLoading, setArchiveLoading] = useState(false);
  const toggleArchiveStatus = async (site) => {
    const accessToken = localStorage.getItem("access");
    if (!accessToken) return;
    setArchiveLoading(true);
    try {
      // const response = await fetch(
      //   `${BASE_URL}/sites/update-sites/${site.id}/`,
      //   {
      //     method: "PATCH",
      //     headers: {
      //       "Content-Type": "application/json",
      //       "X-CSRFToken": csrftoken,
      //       Authorization: `Bearer ${accessToken}`,
      //     },
      //     body: JSON.stringify({
      //       active_flag: !site.active_flag,
      //     }),
      //   }
      // );
      // console.log("Toggling archive status for site:", site);

      const res = await ProjectApi.updateSiteArchiveStatus({
        siteId: site.id,
        activeFlag: !site.active_flag,
      });
      if (res.status) {
        setProjects((prevProjects) =>
          prevProjects.map((p) =>
            p.id === site.id ? { ...p, active_flag: !site.active_flag } : p,
          ),
        );
      } else {
        console.error("Failed to update site archive status");
      }
    } catch (error) {
      console.error("Error updating site archive status:", error);
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearch("");
  };

  async function fetchProjects() {
    if (projects.length === 0) {
      setLoading(true);
    }
    const organization_id = organizationIdProp || getOrganizationId();
    const accessToken = localStorage.getItem("access");
    if (!organization_id || !accessToken) {
      return;
    }
    try {
      // Fetch all projects for the user
      const response = await ProjectApi.listSitesByUser();
      if (!response || !response.status) {
        setLoading(false);
        return;
      }

      const sitesData = response.data || [];

      // Filter by organization_id and map to required format
      const filtered = sitesData
        .filter((site) => site.organization === organization_id)
        .map((site) => ({
          ...site,
          name: site.site_name,
          users: site.members || [],
        }));

      setProjects(filtered);
      localStorage.setItem("projects", JSON.stringify(filtered));
      window._projects = filtered;
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    fetchProjects();
  }, [organizationIdProp]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setSortDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  let filteredProjects = projects.filter(
    (p) =>
      // Check if project matches active/archived tab
      (activeTab === "archived"
        ? p.active_flag === false
        : p.active_flag !== false) &&
      // Then apply search
      (p.name || "").toLowerCase().includes(search.toLowerCase()),
  );

  if (sort === "Name") {
    filteredProjects = [...filteredProjects].sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    );
  } else if (sort === "Budget") {
    filteredProjects = [...filteredProjects].sort((a, b) => {
      const aBudget = parseFloat(
        (a.budget || a.estimated_budget || "0")
          .toString()
          .replace(/[^\d.]/g, ""),
      );
      const bBudget = parseFloat(
        (b.budget || b.estimated_budget || "0")
          .toString()
          .replace(/[^\d.]/g, ""),
      );
      return bBudget - aBudget;
    });
  } else if (sort === "Recent") {
    const recentIds = getRecentProjectIds();
    filteredProjects = [...filteredProjects].sort((a, b) => {
      const aIndex = recentIds.indexOf(a.id);
      const bIndex = recentIds.indexOf(b.id);

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      return 0;
    });
  }

  // If organization is not set and we're not loading (edge case), show empty state
  if (!organizationIdProp && !getOrganizationId()) {
    return <OrganizationEmptyState />;
  }

  return (
    <div className="pl-2 pr-4 pt-3 pb-0" style={{ flex: 1 }}>
      <LoadingScreen loading={loading}>
        <div className="flex border-b border-gray-200 mb-6">
          <button
            className={`py-2 px-4 font-medium text-sm ${activeTab === "active"
              ? "text-purple-600 border-b-2 border-purple-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
            onClick={() => handleTabChange("active")}
          >
            Active Projects
          </button>
          <button
            className={`py-2 px-4 font-medium text-sm ${activeTab === "archived"
              ? "text-purple-600 border-b-2 border-purple-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
            onClick={() => handleTabChange("archived")}
          >
            Archived Projects
          </button>
        </div>

        <div className={styles.toolbarContainer}>
          <div className={styles.toolbarLeft}>
            <span className="font-medium text-[#737373] text-sm">
              {filteredProjects.length}{" "}
              {activeTab === "archived" ? "Archived" : "Active"} Project
              {filteredProjects.length !== 1 ? "s" : ""}
            </span>

            <input
              className={styles.searchInput}
              type="text"
              placeholder="Search for something"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className={styles.toolbarRight}>
            <div className={styles.sortDropdown} ref={sortRef}>
              <button
                className={styles.sortBtn}
                onClick={() => setSortDropdown((s) => !s)}
                type="button"
              >
                <img
                  src={sortIcon}
                  alt="sort"
                  style={{ width: 20, height: 20 }}
                />
                Sorted by {sort}
              </button>
              {sortDropdown && (
                <div className={styles.dropdownMenu}>
                  {["Recent", "Budget", "Name"].map((option) => (
                    <div
                      key={option}
                      onClick={() => {
                        setSort(option);
                        setSortDropdown(false);
                      }}
                      className={styles.dropdownItem}
                      onMouseDown={(e) => e.preventDefault()}
                      style={{
                        color: sort === option ? "#3B0069" : "#333",
                        fontWeight: sort === option ? 600 : 400,
                      }}
                    >
                      {option}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* <button className={styles.toggleBtn}>⠇</button> */}

            <button
              className={styles.newProjectBtn}
              onClick={() => setShowProjectForm(true)}
              style={{
                display:
                  currentMember?.project_control === false &&
                    currentMember?.joined_as !== "owner" &&
                    currentMember?.is_admin === false
                    ? "none"
                    : "flex",
              }}
            >
              + New Project
            </button>
          </div>
        </div>

        <div className={styles.projectsTable}>
          {filteredProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10">
              <img
                src={ProjectEmptyIcon}
                alt="No Projects"
                className="mb-4"
                width={240}
              />
              <span className="text-gray-500 text-2xl">No Projects</span>
              <span className="text-gray-500 text-sm">
                Add a Project to get started
              </span>
            </div>
          ) : (
            <div className="px-2">
              {filteredProjects.map((project, idx) => {
                const addressParts = [
                  project.address || project.site_address || project.location,
                  project.pincode || project.pin_code || project.zip_code,
                ].filter(Boolean);
                const address =
                  addressParts.length > 0
                    ? addressParts.join(", Pin ")
                    : "Address not available";
                const members = project.users || [];
                const status =
                  project.status || project.constructionStatus || "-";
                // Show up to 4 avatars, then a +N badge
                const maxAvatars = 4;
                const extraMembers =
                  members.length > maxAvatars ? members.length - maxAvatars : 0;
                return (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1.5px solid #78797A",
                      borderRadius: "12px",
                      marginBottom: "12px",
                      padding: "12px 24px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                    }}
                    onClick={() => {
                      recordProjectAccess(project.id);
                      localStorage.setItem("site", project.id);
                      localStorage.setItem("siteName", project.site_name);
                      localStorage.setItem("siteData", JSON.stringify(project));
                      navigate(`/site/${project.id}/home`);
                    }}
                    className={styles.projectCard}
                  >
                    <div style={{ flex: 2, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 16,
                          color: "#262B36",
                        }}
                      >
                        {project.name || "Project Name"}
                      </div>
                      <div
                        style={{
                          color: "#888",
                          fontSize: 13,
                          marginTop: 1,
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                        }}
                      >
                        {address}
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 120, textAlign: "right" }}>
                      <div style={{ color: "#888", fontSize: 12 }}>
                        Estimated Budget
                      </div>
                      <div
                        style={{
                          fontWeight: 500,
                          fontSize: 14,
                          color: "#262B36",
                        }}
                      >
                        {project.esrimated_cost ?
                          "₹ " + formatCurrency(project.esrimated_cost) : "Budget not set"}
                      </div>
                    </div>
                    {/* <div style={{ flex: 1, minWidth: 120, textAlign: "right" }}>
                    <div style={{ color: "#888", fontSize: 13 }}>
                      Total Expense
                    </div>
                    <div
                      style={{
                        fontWeight: 500,
                        fontSize: 16,
                        color: "#262B36",
                      }}
                    >
                      {project.expense ||
                        project.total_expense ||
                        "₹ 21,00,500"}
                    </div>
                  </div> */}
                    <div
                      className={styles.teamAvatars}
                      style={{
                        flex: 1,
                        minWidth: 120,
                        justifyContent: "flex-end",
                      }}
                    >
                      {members.slice(0, maxAvatars).map((user, i) => (
                        user.user_details?.userpic ? (
                          <img
                            key={i}
                            src={user.user_details?.userpic || avatar}
                            alt="avatar"
                            className={styles.teamAvatar}
                            style={{ marginLeft: i === 0 ? 0 : -10 }}
                          />) : (
                          <div
                            key={i}
                            className="flex flex-col items-center justify-center text-xs rounded-full bg-sm-primary text-white border-[0.8px] border-white p-1"
                            style={{ marginLeft: i === 0 ? 0 : -5 }}
                          >
                            {user.user_details?.first_name[0] + user.user_details?.last_name[0]}
                          </div>
                        )
                      ))}
                      {extraMembers > 0 && (
                        <span
                          style={{
                            marginLeft: 4,
                            background: "#ECE6F0",
                            color: "#3B0069",
                            borderRadius: "50%",
                            width: 26,
                            height: 26,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 600,
                            fontSize: 12,
                            border: "2px solid #fff",
                          }}
                        >
                          +{extraMembers}
                        </span>
                      )}
                    </div>
                    <div style={{ flex: 0, minWidth: 100, textAlign: "right" }}>
                      <span
                        className={styles.statusBadge}
                        style={{
                          background: statusColors[status] || "#999",
                          minWidth: 72,
                          display: "inline-block",
                          textAlign: "center",
                        }}
                      >
                        {status === "Ongoing"
                          ? "Ongoing"
                          : status === "Completed" || status === "Complete"
                            ? "Complete"
                            : status === "Not Started"
                              ? "Not Started"
                              : status}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!archiveLoading) toggleArchiveStatus(project);
                      }}
                      style={{
                        display:
                          currentMember?.project_control === false &&
                            currentMember?.joined_as !== "owner" &&
                            currentMember?.is_admin === false
                            ? "none"
                            : "flex",
                      }}
                      className={`${archiveLoading
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                        } px-3 py-1 ml-2 text-md font-semibold flex flex-row items-center  rounded-lg border-[1px] border-btn-bg text-btn-bg hover:bg-gray-200`}
                      disabled={archiveLoading}
                    >
                      {project.active_flag ? (
                        <MdOutlineArchive
                          style={{ marginRight: 4, fontSize: 18 }}
                        />
                      ) : (
                        <MdOutlineUnarchive
                          style={{ marginRight: 4, fontSize: 18 }}
                        />
                      )}
                      {project.active_flag === false ? "Unarchive" : "Archive"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </LoadingScreen>
      {showProjectForm && (
        <div className={styles.modalOverlay}>
          <NewSiteForm
            open={showProjectForm}
            onClose={() => {
              setShowProjectForm(false);
              fetchProjects();
            }}
            onSubmit={() => {
              setShowProjectForm(false);
              fetchProjects();
            }}
          />
        </div>
      )}
    </div>
  );
}
